import React, { useMemo } from 'react'
import dynamic from 'next/dynamic'
import { responseToBlogPostProps } from './responseToBlogPostProps'
import ContentTemplate from '../../shared/ContentTemplate/index'
import H1 from '../../shared/Headings/H1'
import RichText from '../../shared/RichText'
import Accordion from '../../shared/FAQ/Accordion'
import TableOfContent from '../../shared/TableOfContent'
import styles from '../ArticleLayout/index.module.scss'
import RecommendedReads from '../../shared/RecommendedReads'
import { addHyperlinkTitle } from '../../../common/helpers'
import DataStrip from 'src/components/shared/DataStrip'
import Img from 'src/components/shared/Img'
import LazyLoad from 'react-lazyload'
import ReviewedBy from 'src/components/shared/ReviewedBy'
import EnhancedVideoPlayer from 'src/components/shared/EnhancedVideoPlayer'
import { ArticleProvider } from 'src/components/shared/ArticleProvider'
import { getArticleCtaVariations } from 'src/components/shared/CustomerConceptLeadCTA/getArticleCtaVariations'
import { resolveFaqBlockTitle } from 'src/common/resolveFaqBlockTitle'

const CustomerConceptLeadCTA = dynamic(() =>
  import('src/components/shared/CustomerConceptLeadCTA')
)

const Breadcrumb = dynamic(() => import('../../shared/Breadcrumb'))

const PureBlogPostLayout = ({
  breadcrumbs,
  image,
  title,
  author,
  modifiedDate,
  keyTakeaways,
  content,
  dataStrip,
  dataStripSource,
  faq,
  recommendedReads,
  tableOfContent,
  customPublishedDate,
  trustMarkersCombination,
  reviewer,
  videoUrl,
  faqTitle,
}) => {
  addHyperlinkTitle(content.content, title)
  const randomCTAs = useMemo(() => getArticleCtaVariations(title), [title])

  return (
    <ArticleProvider articleTitle={title}>
      <div className="bg-gray-linear-xs pt-3.5 md:bg-gray-linear-md lg:pt-8">
        <div className="w-content gap-x-primary padding-x-primary grid grid-cols-4 md:grid-cols-8 lg:grid-cols-12">
          <div
            className="col-span-4 md:col-span-8 lg:col-span-10 lg:col-start-2"
            id="top-section"
          >
            <Breadcrumb items={breadcrumbs} />
            <H1
              text={title}
              id={title}
              className="mt-5 text-black-base lg:mt-9"
            />
          </div>
          <div className="col-span-4 md:col-span-8 lg:col-start-3">
            <div className="mb-4">
              <Img
                priority={true}
                src={image.path}
                layout="fill"
                customLoader={false}
                objectFit="cover"
                alt={image.label}
                imgClassName="rounded-xl"
                isBackgroundImage={false}
                className="h-[150px] w-full md:h-[250px] lg:h-[350px]"
                styles={{ position: 'relative' }}
              />
            </div>
            <div className="mb-4 flex flex-col uppercase md:flex-row">
              <div className="flex">
                <pre className="block text-sm md:text-base">By </pre>
                <ContentTemplate.Author
                  className="border-black-base pr-3 text-sm md:border-r md:text-base"
                  fullName={author.fullName}
                  url={author.url}
                  showPrefixText={false}
                />
              </div>
              {reviewer && (
                <div className="flex pt-1 md:pt-0">
                  <pre className="block text-sm md:text-base">
                    {' '}
                    Reviewed By{' '}
                  </pre>
                  <ContentTemplate.Author
                    className="border-black-base pr-3 text-sm md:border-r md:text-base"
                    fullName={reviewer.fullName}
                    url={reviewer.url}
                    showPrefixText={false}
                  />
                </div>
              )}
              <div className="flex pt-1 md:pt-0">
                <pre className="block text-sm md:text-base"> Updated </pre>
                <ContentTemplate.ModifiedDate
                  ignoreHours
                  short
                  className="text-sm md:text-base"
                  date={modifiedDate || customPublishedDate}
                />
              </div>
            </div>
            {keyTakeaways.length ? (
              <ContentTemplate.KeyTakeaways list={keyTakeaways} />
            ) : null}
            {tableOfContent.length ? (
              <TableOfContent list={tableOfContent} />
            ) : null}
            {videoUrl && (
              <div className="mb-8">
                <EnhancedVideoPlayer
                  src={videoUrl}
                  title={title}
                  aspectRatio="16:9"
                  controls={true}
                  light={true}
                  lazy={true}
                />
              </div>
            )}
            <CustomerConceptLeadCTA
              trustMarkersCombination={trustMarkersCombination}
              className="mt-8"
              variation={randomCTAs[0]}
            />
            <div className="padding-t-primary">
              <RichText
                title={title}
                contentData={content}
                className={styles.richText}
              />
              <DataStrip dataStrip={dataStrip} source={dataStripSource} />
              <LazyLoad>
                <CustomerConceptLeadCTA
                  trustMarkersCombination={trustMarkersCombination}
                  className={'mt-8'}
                  variation={randomCTAs[1]}
                />
              </LazyLoad>
              <ReviewedBy author={author} reviewedBy={reviewer} />
              <div className="margin-t-primary">
                <Accordion
                  showFirstDivider
                  questionClassName="lg:pb-8"
                  title={resolveFaqBlockTitle(faqTitle)}
                  items={faq}
                />
              </div>
              {recommendedReads && recommendedReads.length ? (
                <RecommendedReads readList={recommendedReads} />
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </ArticleProvider>
  )
}

const BlogPostLayout = responseToBlogPostProps(PureBlogPostLayout)

export default React.memo(BlogPostLayout)
